import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  useAddCreatorToTargetCollaboration,
  useBulkAddCreatorsToTargetCollaboration,
  useTargetCollaborations,
} from "@/api/collaborations";
import { BulkInviteResultsDialog } from "@/components/assurance/bulk-invite-results-dialog";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useBulkActionContext } from "@/providers/bulk-action-progress-provider";
import { error, success } from "@/providers/ToastProvider";

type Creator = {
  openId: string;
  username: string;
  nickname?: string | null;
  avatar?: string | null;
};

export const InviteToCollaborationModal = ({
  creator,
  creators,
  children,
  onSuccess,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  excludeCollaborationId,
  preselectedCollaborationId,
  nbaModelVersion,
}: {
  creator?: Creator; // Single creator (backward compatible)
  creators?: Creator[]; // Multiple creators (new)
  children?: ReactNode;
  // Receives the freshly published collab id when the target was a draft, so the
  // caller can record analytics against the real id rather than the now-stale draft id.
  onSuccess?: (publishedCollaborationId?: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  excludeCollaborationId?: string; // Exclude a specific collaboration from the list
  preselectedCollaborationId?: string; // Pre-select and lock a specific collaboration
  /** See QuickInviteModalProps.nbaModelVersion — same behaviour. */
  nbaModelVersion?: string;
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (controlledOnOpenChange ?? (() => {})) : setInternalOpen;
  const [selected, setSelected] = useState<string | null>(preselectedCollaborationId ?? null);
  const [sendDM, setSendDM] = useState(false);
  const isPreselected = !!preselectedCollaborationId;
  const [resultsDialog, setResultsDialog] = useState<{
    results: Array<{ creatorUsername: string; success: boolean; reason?: string }>;
    wasDraftPublished: boolean;
  } | null>(null);

  // Determine if single or multiple creators
  const creatorsList = useMemo(() => {
    if (creators && creators.length > 0) return creators;
    if (creator) return [creator];
    return [];
  }, [creator, creators]);

  const isSingleCreator = creatorsList.length === 1;
  const creatorCount = creatorsList.length;

  // Only fetch collaborations when the modal is open to avoid unnecessary API calls
  const { data: collaborations, isLoading } = useTargetCollaborations(creatorsList[0]?.username || "", open);
  const { mutateAsync: inviteCreator, isPending: isSinglePending } = useAddCreatorToTargetCollaboration();
  const { mutateAsync: bulkInviteCreators, isPending: isBulkPending } = useBulkAddCreatorsToTargetCollaboration();
  const { startTracking: startBulkTracking } = useBulkActionContext();

  const isPending = isSinglePending || isBulkPending;

  const displayName = useMemo(() => {
    if (isSingleCreator) {
      return creatorsList[0]?.nickname || creatorsList[0]?.username || "";
    }
    return null;
  }, [isSingleCreator, creatorsList]);

  // Reset selection when modal opens (respect preselection)
  useEffect(() => {
    if (open) {
      setSelected(preselectedCollaborationId ?? null);
      setSendDM(false);
    }
  }, [open, preselectedCollaborationId]);

  // Show only collaborations that can accept more creators:
  //   - not the excluded collab (if any)
  //   - fewer than 50 invited creators (TikTok Shop hard cap per collaboration)
  //   - draft collabs (creator_inivited_count is 0 and the cap check is done at publish-time)
  const eligibleCollaborations = useMemo(() => {
    if (!collaborations) return [];
    return collaborations.filter((collab) => {
      if (excludeCollaborationId && collab.id === excludeCollaborationId) return false;
      const isDraft = collab.id.startsWith("draft_");
      if (!isDraft && collab.creator_inivited_count >= 50) return false;
      return true;
    });
  }, [collaborations, excludeCollaborationId]);

  const onSubmit = async () => {
    if (!selected) {
      error("Please select a collaboration");
      return;
    }

    const isDraftCollab = selected.startsWith("draft_");

    try {
      let publishedCollaborationId: string | undefined;
      let showResultsDialogFor: {
        results: Array<{ creatorUsername: string; success: boolean; reason?: string }>;
        wasDraftPublished: boolean;
      } | null = null;

      if (isSingleCreator) {
        const result = await inviteCreator({
          targetCollaborationId: selected,
          creatorOpenId: creatorsList[0].openId,
          sendDM,
          ...(nbaModelVersion && { nbaModelVersion }),
        });
        if ("publishedCollaborationId" in result) {
          publishedCollaborationId = result.publishedCollaborationId;
        }
        if (isDraftCollab) {
          success(`Draft collaboration published to TikTok and ${displayName || creatorsList[0].username} was invited`);
        } else {
          success(`Added ${displayName || creatorsList[0].username} to collaboration`);
        }
      } else {
        const result = await bulkInviteCreators({
          targetCollaborationId: selected,
          creatorOpenIds: creatorsList.map((c) => c.openId),
          sendDM,
          ...(nbaModelVersion && { nbaModelVersion }),
        });

        if ("async" in result) {
          startBulkTracking(result.jobId, "invite_to_collaboration", null);
          onSuccess?.();
          setOpen(false);
          return;
        }

        const { summary, results } = result;
        publishedCollaborationId = result.publishedCollaborationId;
        const wasDraftPublished = !!publishedCollaborationId;

        if (summary.succeeded === summary.total) {
          if (isDraftCollab) {
            success(
              `Draft collaboration published to TikTok and ${summary.succeeded} creator${summary.succeeded > 1 ? "s" : ""} invited`,
            );
          } else {
            success(
              `Successfully invited ${summary.succeeded} creator${summary.succeeded > 1 ? "s" : ""} to collaboration`,
            );
          }
        } else {
          // Partial failure (all-fail throws server-side and lands in catch). Map the API's
          // open-id-keyed results back to usernames using the input list so the dialog can
          // render @{handle} rows.
          const usernameByOpenId = new Map(creatorsList.map((c) => [c.openId, c.username]));
          const enrichedResults = results.map((r) => ({
            creatorUsername: usernameByOpenId.get(r.creatorOpenId) ?? r.creatorOpenId,
            success: r.success,
            reason: r.reason,
          }));
          showResultsDialogFor = { results: enrichedResults, wasDraftPublished };
        }
      }

      onSuccess?.(publishedCollaborationId);
      setOpen(false);
      if (showResultsDialogFor) setResultsDialog(showResultsDialogFor);
    } catch (err) {
      error(err instanceof Error ? err.message : "Failed to add creator to collaboration");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {children && <DialogTrigger asChild>{children}</DialogTrigger>}
        <DialogContent className="sm:max-w-[40rem] max-h-[85vh] flex flex-col overflow-hidden">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            className="space-y-6 flex flex-col flex-1 min-h-0 overflow-hidden"
          >
            <DialogHeader className="shrink-0">
              <DialogTitle>
                {isSingleCreator && displayName
                  ? `Invite ${displayName} to a collaboration`
                  : `Invite ${creatorCount} creators to collaboration`}
              </DialogTitle>
            </DialogHeader>

            {isPreselected ? (
              <div className="flex items-center gap-3 bg-secondary rounded-lg px-4 py-3 shrink-0">
                <Checkbox isSelected={true} className="pointer-events-none" />
                <span className="text-sm text-primary">
                  {collaborations?.find((c) => c.id === preselectedCollaborationId)?.name ?? "Selected collaboration"}
                </span>
              </div>
            ) : (
              <div className="flex flex-col flex-1 min-h-0 rounded-lg overflow-hidden">
                {/* Table Header — pinned, always visible */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary bg-secondary shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-5" /> {/* Spacer for checkbox alignment */}
                    <span className="text-sm text-tertiary flex items-center gap-1">Collaboration name</span>
                  </div>
                  <span className="text-sm text-tertiary"># Invites left to send</span>
                </div>

                {/* Table Body — the only scrollable area */}
                <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
                  {isLoading ? (
                    <div
                      className="flex flex-col items-center justify-center py-16 px-6 min-h-[25rem] rounded-lg"
                      style={{
                        background: "linear-gradient(135deg, rgb(229 247 255 / 60%) 0%, rgb(255 221 244 / 60%) 100%)",
                      }}
                    >
                      <p className="text-sm text-gray-600">Loading collaborations...</p>
                    </div>
                  ) : !eligibleCollaborations || eligibleCollaborations.length === 0 ? (
                    <div
                      className="flex flex-col items-center justify-center py-16 px-6 min-h-[25rem] rounded-lg"
                      style={{
                        background: "linear-gradient(135deg, rgb(229 247 255 / 60%) 0%, rgb(255 221 244 / 60%) 100%)",
                      }}
                    >
                      <h3 className="text-lg font-semibold text-start text-gray-900 mb-2">
                        No Collaborations available
                      </h3>
                      <p className="text-md text-gray-600 text-start max-w-sm w-[250px]">
                        Please go to TikTok and create a collaboration to populate this list.
                      </p>
                    </div>
                  ) : (
                    eligibleCollaborations.map((collab) => {
                      const isCollabDraft = collab.id.startsWith("draft_");
                      const invitesLeft = isCollabDraft ? 50 : 50 - collab.creator_inivited_count;
                      const isSelected = selected === collab.id;
                      return (
                        <button
                          key={collab.id}
                          type="button"
                          onClick={() => setSelected(isSelected ? null : collab.id)}
                          className="flex items-center justify-between px-4 py-4 border-b border-border-secondary last:border-b-0 cursor-pointer hover:bg-secondary/50"
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              isSelected={isSelected}
                              className="pointer-events-none"
                              onClick={(e) => e.preventDefault()}
                            />
                            <span className="text-sm text-primary">{collab.name}</span>
                            {isCollabDraft && (
                              <Badge color="brand" size="sm">
                                Unbox Draft
                              </Badge>
                            )}
                          </div>
                          <span className="text-sm text-primary">{invitesLeft}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Only show notification and additional message sections after selecting a collaboration */}
            {(selected || isPreselected) && (
              <>
                {/* Notification info - shows avatar for single creator, count badge for multiple */}
                <div className="flex items-center gap-3 bg-secondary rounded-lg px-4 py-3 shrink-0">
                  {isSingleCreator && creatorsList[0] ? (
                    <>
                      {creatorsList[0].avatar ? (
                        <img
                          src={creatorsList[0].avatar}
                          alt={displayName || "Creator"}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {(displayName || "?").charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <span className="text-sm text-primary">{displayName} will be notified on TikTok</span>
                    </>
                  ) : (
                    <>
                      <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">{creatorCount}</span>
                      </div>
                      <span className="text-sm text-primary">
                        {creatorCount === 1 ? "creator" : "creators"} will be notified on TikTok
                      </span>
                    </>
                  )}
                </div>

                {/* DM checkbox */}
                <div className="flex flex-col gap-3 shrink-0">
                  <span className="text-sm text-primary font-medium">Want to send an additional message?</span>
                  <label
                    htmlFor="send-dm"
                    className="flex items-center gap-3 border border-border-secondary rounded-lg px-4 py-3 cursor-pointer"
                  >
                    <Checkbox id="send-dm" isSelected={sendDM} onChange={(checked) => setSendDM(checked === true)} />
                    <span className="text-sm text-primary">Include a card invite in their TikTok DMs</span>
                  </label>
                </div>
              </>
            )}

            <DialogFooter className="shrink-0">
              <Button color="secondary" size="lg" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button isDisabled={isPending || !selected} isLoading={isPending} color="primary" size="lg" type="submit">
                Invite to collaboration
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <BulkInviteResultsDialog
        open={!!resultsDialog}
        onOpenChange={(o) => {
          if (!o) setResultsDialog(null);
        }}
        results={resultsDialog?.results ?? []}
        wasDraftPublished={resultsDialog?.wasDraftPublished ?? false}
      />
    </>
  );
};
